// Column registry for all configurable list views.
// Each column: { id, label, accessor(row), sortable?, defaultWidth?, headerClass?, cellClass?, render?(row) }
// Default visible arrays match the spec exactly.

import React from "react";
import { Link } from "react-router-dom";
import StatusBadge, { PriorityBadge, EscalationBadge } from "../components/StatusBadge.jsx";
import { fmtDate, fmtDateTime } from "./format.js";

function daysBetween(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

// Feedback chip rendered as a colored dot
function FeedbackDot({ status }) {
  const color =
    status === "Red" ? "#DC3545" :
    status === "Yellow" ? "#F59E0B" :
    status === "Green" ? "#28A745" : "#6C757D";
  return <span title={status || "—"} className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />;
}

// === Service Cases ===
export const SERVICE_CASES_COLUMNS = [
  { id: "number", label: "Nº SOM", sortable: true, defaultWidth: 110, render: (r) => (
      <Link to={`/service-cases/${r.id}`} className="font-mono text-[#1F3864] font-semibold hover:underline">{r.number}</Link>) },
  { id: "alidata_job_card", label: "Alidata", sortable: true, defaultWidth: 130, accessor: (r) => r.alidata_job_card },
  { id: "customer_name", label: "Cliente", sortable: true, defaultWidth: 180, accessor: (r) => r.customer_name },
  { id: "customer_phone", label: "Telefone", defaultWidth: 130, accessor: (r) => r.customer_phone || "—" },
  { id: "vehicle_registration", label: "Matrícula", sortable: true, defaultWidth: 120, accessor: (r) => r.vehicle_registration },
  { id: "vin", label: "VIN", defaultWidth: 180, accessor: (r) => r.vin },
  { id: "brand", label: "Marca", sortable: true, defaultWidth: 100, accessor: (r) => r.brand },
  { id: "model", label: "Modelo", defaultWidth: 120, accessor: (r) => r.model },
  { id: "branch", label: "Filial", sortable: true, defaultWidth: 110, accessor: (r) => r.branch },
  { id: "service_advisor", label: "Service Advisor", defaultWidth: 150, accessor: (r) => r.service_advisor?.name || "—" },
  { id: "workshop_supervisor", label: "Supervisor", defaultWidth: 150, accessor: (r) => r.workshop_supervisor?.name || "—" },
  { id: "current_status", label: "Estado", sortable: true, defaultWidth: 200, render: (r) => <StatusBadge status={r.current_status} /> },
  { id: "priority", label: "Prioridade", sortable: true, defaultWidth: 110, render: (r) => <PriorityBadge priority={r.priority} /> },
  { id: "feedback_status", label: "Feedback", defaultWidth: 90, render: (r) => <FeedbackDot status={r.feedback_status} /> },
  { id: "last_customer_contact", label: "Último contacto", sortable: true, defaultWidth: 140, accessor: (r) => fmtDate(r.last_customer_contact) },
  { id: "last_contact_method", label: "Método", defaultWidth: 110, accessor: (r) => r.last_customer_contact_method || "—" },
  { id: "next_action", label: "Próxima acção", defaultWidth: 220, accessor: (r) => r.next_action || "—" },
  { id: "next_action_responsible", label: "Responsável", defaultWidth: 150, accessor: (r) => r.next_action_responsible?.name || "—" },
  { id: "next_action_deadline", label: "Deadline", sortable: true, defaultWidth: 140, accessor: (r) => fmtDateTime(r.next_action_deadline) },
  { id: "opened_at", label: "Aberta em", sortable: true, defaultWidth: 130, accessor: (r) => fmtDate(r.opened_at) },
  { id: "days_open", label: "Dias abertos", sortable: true, defaultWidth: 100, accessor: (r) => daysBetween(r.opened_at) ?? "—" },
  { id: "escalation_level", label: "Escalação", sortable: true, defaultWidth: 100, accessor: (r) => r.escalation_level ?? 0 },
  { id: "parts_requests_count", label: "PECs", defaultWidth: 70, accessor: (r) => r.parts_requests_count ?? 0 },
  { id: "tasks_count", label: "Tarefas", defaultWidth: 80, accessor: (r) => r.tasks_count ?? 0 },
];

export const SERVICE_CASES_DEFAULT = [
  "number", "customer_name", "vehicle_registration", "brand",
  "service_advisor", "current_status", "priority", "feedback_status",
  "next_action_deadline", "days_open",
];

// === Tasks ===
const ROW_HIGHLIGHT_SELECTED = "bg-[#FFFBEB]"; // exported for TasksPage via getRowClassName

export function tasksRowClass(row, ctx) {
  if (!ctx) return "";
  return ctx.selected && ctx.selected.has(row.id) ? ROW_HIGHLIGHT_SELECTED : "";
}

export const TASKS_COLUMNS = [
  // Bulk-select checkbox (manager-only). Rendered as empty cell for non-managers
  // so the column still occupies the slot when configured visible.
  { id: "bulk_select", label: "", defaultWidth: 36, headerClass: "w-8",
    render: (r, ctx) => {
      if (!ctx?.isManager) return null;
      const eligible = !["Concluída", "Cancelada"].includes(r.status);
      if (!eligible) return null;
      return (
        <input
          type="checkbox"
          checked={ctx.selected.has(r.id)}
          onChange={(e) => { e.stopPropagation(); ctx.toggleOne(r.id); }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`bulk-select-${r.id}`}
        />
      );
    } },
  { id: "number", label: "Nº TRF", sortable: true, defaultWidth: 110, accessor: (r) => r.number,
    render: (r) => <span className="font-mono text-[#1F3864] font-semibold">{r.number}</span> },
  { id: "title", label: "Título", sortable: true, defaultWidth: 280,
    accessor: (r) => r.title,
    render: (r) => (
      <div>
        <div className="text-[#1A1A2E]">{r.title}</div>
        {r.auto_generated && <span className="text-[9px] uppercase tracking-widest text-[#6C757D]">Auto</span>}
      </div>
    ) },
  { id: "linked_case", label: "Obra associada", sortable: true, defaultWidth: 200,
    accessor: (r) => r.linked_case?.number || "",
    render: (r) => (
      r.linked_case ? (
        <Link to={`/service-cases/${r.linked_case.id}`} className="block hover:text-[#1F3864] text-xs" onClick={(e) => e.stopPropagation()} data-testid={`task-case-link-${r.id}`}>
          <span className="font-mono text-[#1F3864] font-semibold">{r.linked_case.number}</span>
          <span className="block text-[#1A1A2E]">{r.linked_case.customer_name}</span>
          <span className="block text-[10px] text-[#6C757D]">{r.linked_case.branch}</span>
        </Link>
      ) : (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#E2E8F0] text-[#495057] px-1.5 py-0.5 rounded-sm font-semibold">Standalone</span>
      )
    ) },
  { id: "service_case_number", label: "Obra (nº)", sortable: true, defaultWidth: 110,
    render: (r) => r.service_case_id ? <Link to={`/service-cases/${r.service_case_id}`} className="font-mono text-[#1F3864] hover:underline" onClick={(e) => e.stopPropagation()}>{r.service_case_number || "—"}</Link> : "—" },
  { id: "customer_name", label: "Cliente", defaultWidth: 180, accessor: (r) => r.customer_name || r.linked_case?.customer_name || "—" },
  { id: "vehicle_registration", label: "Matrícula", defaultWidth: 120, accessor: (r) => r.vehicle_registration || "—" },
  { id: "linked_module", label: "Módulo", sortable: true, defaultWidth: 110, accessor: (r) => r.linked_module },
  { id: "responsible_user", label: "Responsável", defaultWidth: 160,
    accessor: (r) => r.responsible_user?.name || "",
    render: (r) => r.responsible_user ? (
      <div className="text-xs">
        <div className="text-[#1A1A2E]">{r.responsible_user.name}</div>
        <div className="text-[10px] text-[#6C757D]">{(r.responsible_user.role || "").replaceAll("_", " ")}</div>
      </div>
    ) : "—" },
  { id: "responsible_department", label: "Departamento", sortable: true, defaultWidth: 130, accessor: (r) => r.responsible_department },
  { id: "priority", label: "Prioridade", sortable: true, defaultWidth: 110, render: (r) => <PriorityBadge priority={r.priority} /> },
  { id: "deadline", label: "Deadline", sortable: true, defaultWidth: 140, accessor: (r) => r.deadline, render: (r) => <span className="whitespace-nowrap">{fmtDateTime(r.deadline)}</span> },
  { id: "status", label: "Estado", sortable: true, defaultWidth: 130, render: (r) => <StatusBadge status={r.status} /> },
  { id: "escalation_level", label: "Escalação", sortable: true, defaultWidth: 100,
    accessor: (r) => r.escalation_level ?? 0,
    render: (r) => <EscalationBadge level={r.escalation_level} /> },
  { id: "days_overdue", label: "Dias atraso", sortable: true, defaultWidth: 100,
    accessor: (r) => r.days_overdue ?? 0,
    render: (r) => r.days_overdue > 0 ? (
      <span className="text-[#DC3545] font-semibold whitespace-nowrap" data-testid={`task-days-overdue-${r.id}`}>{r.days_overdue} dia{r.days_overdue === 1 ? "" : "s"}</span>
    ) : <span className="text-[#6C757D]" data-testid={`task-days-overdue-${r.id}`}>—</span> },
  { id: "created_by", label: "Criada por", defaultWidth: 140, accessor: (r) => r.created_by?.name || "—" },
  { id: "created_at", label: "Criada em", sortable: true, defaultWidth: 130, accessor: (r) => r.created_at, render: (r) => fmtDate(r.created_at) },
  { id: "completed_at", label: "Concluída em", sortable: true, defaultWidth: 130, accessor: (r) => r.completed_at, render: (r) => fmtDate(r.completed_at) || "—" },
  { id: "actions", label: "Acções", defaultWidth: 110, cellClass: "text-right",
    render: (r, ctx) => {
      if (!ctx?.onCompleteTask) return null;
      if (["Concluída", "Cancelada"].includes(r.status)) return null;
      return (
        <button
          onClick={(e) => { e.stopPropagation(); ctx.onCompleteTask(r); }}
          className="text-[#28A745] hover:underline font-medium text-xs"
          data-testid={`complete-task-${r.id}`}
        >
          Completar
        </button>
      );
    } },
];

export const TASKS_DEFAULT = [
  "bulk_select", "number", "title", "linked_case", "responsible_user",
  "priority", "deadline", "status", "escalation_level", "days_overdue", "actions",
];

// === Parts Requests ===
export const PARTS_REQUESTS_COLUMNS = [
  { id: "number", label: "Nº PEC", sortable: true, defaultWidth: 110,
    render: (r) => <Link to={`/parts-requests/${r.id}`} className="font-mono text-[#1F3864] font-semibold hover:underline">{r.number}</Link> },
  { id: "service_case_number", label: "Obra", sortable: true, defaultWidth: 110,
    render: (r) => r.service_case_id ? <Link to={`/service-cases/${r.service_case_id}`} className="font-mono text-[#1F3864] hover:underline">{r.service_case_number || "—"}</Link> : "—" },
  { id: "customer_name", label: "Cliente", sortable: true, defaultWidth: 180, accessor: (r) => r.customer_name },
  { id: "vehicle_registration", label: "Matrícula", sortable: true, defaultWidth: 120, accessor: (r) => r.vehicle_registration },
  { id: "brand", label: "Marca", sortable: true, defaultWidth: 100, accessor: (r) => r.brand },
  { id: "alidata_job_card", label: "Job card", defaultWidth: 130, accessor: (r) => r.alidata_job_card },
  { id: "alidata_customer_order", label: "Alidata CO", defaultWidth: 130,
    render: (r) => r.alidata_customer_order ? <span className="font-mono text-xs">{r.alidata_customer_order}</span> : <span className="text-[#DC3545] text-xs">— em falta</span> },
  { id: "responsible_parts_user", label: "Resp. Peças", defaultWidth: 150, accessor: (r) => r.responsible_parts_user?.name || "—" },
  { id: "service_advisor", label: "Service Advisor", defaultWidth: 150, accessor: (r) => r.service_advisor?.name || "—" },
  { id: "current_status", label: "Estado", sortable: true, defaultWidth: 180, render: (r) => <StatusBadge status={r.current_status} /> },
  { id: "priority", label: "Prioridade", sortable: true, defaultWidth: 110, render: (r) => <PriorityBadge priority={r.priority} /> },
  { id: "overall_eta", label: "ETA mais próximo", sortable: true, defaultWidth: 160,
    accessor: (r) => r.overall_eta || null,
    render: (r) => {
      if (!r.overall_eta) return <span className="text-[#6C757D]" data-testid={`row-overall-eta-${r.id}`}>—</span>;
      const overdue = new Date(r.overall_eta) < new Date();
      return (
        <span className={overdue ? "text-[#DC3545] font-semibold" : "text-[#1A1A2E]"} data-testid={`row-overall-eta-${r.id}`}>
          {fmtDateTime(r.overall_eta)}
        </span>
      );
    } },
  { id: "created_at", label: "Criada em", sortable: true, defaultWidth: 130, accessor: (r) => fmtDate(r.created_at) },
  { id: "days_open", label: "Dias abertos", sortable: true, defaultWidth: 100, accessor: (r) => daysBetween(r.created_at) ?? "—" },
  { id: "total_lines", label: "Linhas", sortable: true, defaultWidth: 80, accessor: (r) => r.total_lines ?? (r.lines ? r.lines.length : 0) },
  { id: "pending_lines", label: "Pendentes", sortable: true, defaultWidth: 100, accessor: (r) => r.pending_lines ?? 0 },
  { id: "received_lines", label: "Recebidas", sortable: true, defaultWidth: 100, accessor: (r) => r.received_lines ?? 0 },
];

export const PARTS_REQUESTS_DEFAULT = [
  "number", "service_case_number", "customer_name", "vehicle_registration", "brand",
  "responsible_parts_user", "current_status", "overall_eta",
  "days_open", "pending_lines",
];

// === Customer Feedback ===
export const FEEDBACK_COLUMNS = [
  { id: "number", label: "Nº SOM", sortable: true, defaultWidth: 110,
    render: (r) => <Link to={`/service-cases/${r.id}`} className="font-mono text-[#1F3864] font-semibold hover:underline">{r.number}</Link> },
  { id: "customer_name", label: "Cliente", sortable: true, defaultWidth: 180, accessor: (r) => r.customer_name },
  { id: "vehicle_registration", label: "Matrícula", sortable: true, defaultWidth: 120, accessor: (r) => r.vehicle_registration },
  { id: "brand", label: "Marca", sortable: true, defaultWidth: 100, accessor: (r) => r.brand },
  { id: "service_advisor", label: "Service Advisor", defaultWidth: 150, accessor: (r) => r.service_advisor?.name || "—" },
  { id: "last_contact_date", label: "Último contacto", sortable: true, defaultWidth: 140, accessor: (r) => fmtDate(r.last_customer_contact) },
  { id: "last_contact_method", label: "Método", defaultWidth: 110, accessor: (r) => r.last_customer_contact_method || "—" },
  { id: "last_contact_result", label: "Resultado", defaultWidth: 130, accessor: (r) => r.last_customer_contact_result || "—" },
  { id: "feedback_status", label: "Feedback", sortable: true, defaultWidth: 90, render: (r) => <FeedbackDot status={r.feedback_status} /> },
  { id: "days_since_contact", label: "Dias sem contacto", sortable: true, defaultWidth: 130, accessor: (r) => daysBetween(r.last_customer_contact) ?? "—" },
  { id: "next_follow_up", label: "Próximo follow-up", sortable: true, defaultWidth: 150, accessor: (r) => r.next_action_deadline ? fmtDate(r.next_action_deadline) : "—" },
];

export const FEEDBACK_DEFAULT = [
  "number", "customer_name", "vehicle_registration", "service_advisor",
  "feedback_status", "last_contact_date", "days_since_contact", "next_follow_up",
];

// === Dashboard drill-down ===
// Items in the drill-down panel are heterogeneous (service_case / parts_request / task / etc),
// already enriched server-side. We expose all useful fields here.
function recordTypeBadge(rt) {
  if (rt === "parts_request") return <span className="ml-1.5 text-[9px] uppercase tracking-widest bg-[#E0E7FF] text-[#1E3A8A] px-1 py-0.5 rounded-sm">PR</span>;
  if (rt === "task") return <span className="ml-1.5 text-[9px] uppercase tracking-widest bg-[#FFF3CD] text-[#856404] px-1 py-0.5 rounded-sm">Task</span>;
  return null;
}

function fmtDelayHours(hours) {
  if (hours == null || isNaN(hours)) return "—";
  const h = Math.max(0, hours);
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${Math.floor(h)}h`;
  const days = Math.floor(h / 24);
  const rem = Math.round(h - days * 24);
  return `${days}d ${rem}h`;
}

export const DRILLDOWN_COLUMNS = [
  { id: "number", label: "Número", sortable: true, defaultWidth: 130,
    render: (r) => (
      <span className="font-mono text-[#1F3864] font-semibold whitespace-nowrap">
        {r.number}{recordTypeBadge(r.record_type)}
      </span>
    ) },
  { id: "customer", label: "Cliente", sortable: true, defaultWidth: 200, accessor: (r) => r.customer || "—" },
  { id: "title", label: "Título", defaultWidth: 200, accessor: (r) => r.title || "—" },
  { id: "vehicle", label: "Veículo", sortable: true, defaultWidth: 130, accessor: (r) => r.vehicle || "—" },
  { id: "brand", label: "Marca", sortable: true, defaultWidth: 110, accessor: (r) => r.brand || "—" },
  { id: "branch", label: "Filial", sortable: true, defaultWidth: 130, accessor: (r) => r.branch || "—" },
  { id: "service_advisor", label: "Responsável", sortable: true, defaultWidth: 160, accessor: (r) => r.service_advisor || "—" },
  { id: "current_status", label: "Estado actual", sortable: true, defaultWidth: 180, accessor: (r) => r.current_status || "—" },
  { id: "priority", label: "Prioridade", sortable: true, defaultWidth: 110, accessor: (r) => r.priority || "—" },
  { id: "next_action_deadline", label: "Próximo deadline", sortable: true, defaultWidth: 160,
    accessor: (r) => r.next_action_deadline ? fmtDateTime(r.next_action_deadline) : "—" },
  { id: "delay_hours", label: "Atraso", sortable: true, defaultWidth: 110,
    accessor: (r) => r.delay_hours ?? 0,
    render: (r) => <span className="font-medium">{fmtDelayHours(r.delay_hours)}</span> },
  { id: "open", label: "Abrir", defaultWidth: 80,
    render: (r) => (
      <Link
        to={r.navigate_to || "#"}
        className="inline-flex items-center gap-1 px-2 py-1 bg-[#1F3864] hover:bg-[#142542] text-white text-xs font-medium rounded-sm transition-colors"
        data-testid={`drill-down-open-${r.id}`}
      >
        Abrir →
      </Link>
    ) },
];

export const DRILLDOWN_DEFAULT = [
  "number", "customer", "vehicle", "brand", "branch",
  "service_advisor", "current_status", "next_action_deadline", "delay_hours", "open",
];

// === Reports ===
// Each report has different shape. Build a column entry from { key, label, kind }.
// Kinds supported: text|date|number|status|pct|bool
function reportRender(kind) {
  return (val) => {
    if (val == null || val === "") return <span className="text-[#6C757D]">—</span>;
    if (kind === "date") {
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
    if (kind === "pct") return `${val}%`;
    if (kind === "bool") return val ? "Sim" : "Não";
    if (kind === "number") return <span className="font-mono">{val}</span>;
    if (kind === "lost_reasons") {
      if (!Array.isArray(val) || val.length === 0) return <span className="text-[#6C757D]">—</span>;
      return (
        <span className="text-xs">
          {val.map((r, i) => (
            <span key={i} className="inline-block mr-1 bg-[#F8D7DA] text-[#721C24] rounded-sm px-1.5 py-0.5">
              {r.reason} <span className="font-mono">({r.count})</span>
            </span>
          ))}
        </span>
      );
    }
    return String(val);
  };
}

export function buildReportColumns(cols) {
  return cols.map((c) => ({
    id: c.key,
    label: c.label,
    sortable: true,
    defaultWidth: c.kind === "date" ? 130 : c.kind === "number" || c.kind === "pct" ? 110 : 160,
    accessor: (r) => r[c.key],
    render: (r) => reportRender(c.kind)(r[c.key]),
  }));
}

export const VIEW_REGISTRY = {
  service_cases: { columns: SERVICE_CASES_COLUMNS, default: SERVICE_CASES_DEFAULT, defaultSort: { key: "next_action_deadline", dir: "asc" } },
  tasks: { columns: TASKS_COLUMNS, default: TASKS_DEFAULT, defaultSort: { key: "deadline", dir: "asc" } },
  parts_requests: { columns: PARTS_REQUESTS_COLUMNS, default: PARTS_REQUESTS_DEFAULT, defaultSort: { key: "created_at", dir: "desc" } },
  customer_feedback: { columns: FEEDBACK_COLUMNS, default: FEEDBACK_DEFAULT, defaultSort: { key: "last_contact_date", dir: "asc" } },
  dashboard_drilldown: { columns: DRILLDOWN_COLUMNS, default: DRILLDOWN_DEFAULT, defaultSort: { key: "delay_hours", dir: "desc" } },
};

// Allow callers to register a dynamic view (e.g. per-report) at runtime.
export function registerView(viewKey, registry) {
  VIEW_REGISTRY[viewKey] = registry;
}
